import React, { useState } from 'react';
import './Login.css';

interface PublisherLoginProps {
  onLogin: (username: string, userType: string) => void;
  onSwitchToAdmin: () => void;
}

const PublisherLogin: React.FC<PublisherLoginProps> = ({ onLogin, onSwitchToAdmin }) => {
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }));
    setError(''); // Clear error on input change
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validate inputs
      if (!credentials.username || !credentials.password) {
        throw new Error('Please enter both username and password');
      }

      // Call the publisher login API
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3002';
      console.log('Attempting login to:', `${API_BASE_URL}/api/publisher/login`);
      console.log('Username:', credentials.username);

      const response = await fetch(`${API_BASE_URL}/api/publisher/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: credentials.username,
          password: credentials.password
        })
      });

      const data = await response.json();
      console.log('Login response:', data);

      if (!response.ok) {
        console.error('Login failed:', data);
        throw new Error(data.message || 'Login failed');
      }

      // Login successful
      console.log('Checking success:', data.success, 'Publisher:', data.publisher);
      if (data.success && data.publisher) {
        console.log('Login successful! Storing token and calling onLogin');
        // Store the token
        localStorage.setItem('publisherToken', data.token);
        localStorage.setItem('publisherId', data.publisher.id);

        onLogin(data.publisher.username, 'publisher');
        console.log('onLogin called successfully');
      } else {
        console.error('Success check failed:', { success: data.success, hasPublisher: !!data.publisher });
        throw new Error('Invalid response from server');
      }
    } catch (err) {
      console.error('Login error caught:', err);
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-form-container">
        <div className="login-header">
          <h1>Publisher Portal</h1>
          <p>Access your affiliate dashboard</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && (
            <div className="error-banner">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="username">Username</label>
            <input
              type="text"
              id="username"
              name="username"
              value={credentials.username}
              onChange={handleInputChange}
              placeholder="Enter your username"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={credentials.password}
              onChange={handleInputChange}
              placeholder="Enter your password"
              required
              disabled={loading}
            />
          </div>

          <button
            type="submit"
            className="login-button"
            disabled={loading}
          >
            {loading ? 'Signing In...' : 'Sign In'}
          </button>
        </form>

        <div className="login-demo-info">
          <p>New Publisher?</p>
          <p>Login credentials are generated when an admin creates your publisher account.</p>
          <p>Check your welcome email for your username and password.</p>
        </div>

        <div className="login-footer">
          <div className="login-links">
            <button
              type="button"
              className="link-button"
              onClick={onSwitchToAdmin}
            >
              Admin Login
            </button>
            <span className="separator">•</span>
            <button type="button" className="link-button">
              Forgot Password?
            </button>
          </div>

          <div className="login-help">
            <p>Need access? Contact your affiliate manager</p>
          </div>
        </div>
      </div>

      <div className="login-info">
        <div className="info-content">
          <h2>Publisher Dashboard</h2>
          <ul>
            <li>📊 View your performance metrics</li>
            <li>💰 Track earnings and payouts</li>
            <li>🔗 Manage tracking links</li>
            <li>📈 Access detailed reports</li>
            <li>⚙️ Update account settings</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default PublisherLogin;